import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaTasks, FaPlus, FaCheck, FaExclamationCircle, FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { toast } from 'react-toastify';
import CrmTaskService, { CrmTask } from '../../sales/services/CrmTaskService';
import CrmTaskModal from '../../sales/components/CrmTaskModal';
import QuickTaskInput from '../../sales/components/QuickTaskInput';

interface ContactTasksSectionProps {
  partnerId: number;
  partnerName: string;
}

export default function ContactTasksSection({
  partnerId,
  partnerName,
}: ContactTasksSectionProps): React.JSX.Element {
  const navigate = useNavigate();
  const [openTasks, setOpenTasks] = useState<CrmTask[]>([]);
  const [doneTasks, setDoneTasks] = useState<CrmTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [showDone, setShowDone] = useState(false);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [editingTask, setEditingTask] = useState<CrmTask | null>(null);
  const [completingTaskId, setCompletingTaskId] = useState<number | null>(null);

  const loadTasks = useCallback(async () => {
    setLoading(true);
    try {
      const [openData, doneData] = await Promise.all([
        CrmTaskService.list({
          partner_id: partnerId,
          status: 'OPEN,IN_PROGRESS',
        }),
        CrmTaskService.list({
          partner_id: partnerId,
          status: 'DONE',
          due_from: getLast30Days(),
        }),
      ]);

      const sortedOpen = openData.sort((a, b) => {
        const aOverdue = CrmTaskService.isOverdue(a.due_date);
        const bOverdue = CrmTaskService.isOverdue(b.due_date);
        if (aOverdue && !bOverdue) return -1;
        if (!aOverdue && bOverdue) return 1;
        if (!a.due_date && !b.due_date) return 0;
        if (!a.due_date) return 1;
        if (!b.due_date) return -1;
        return new Date(a.due_date).getTime() - new Date(b.due_date).getTime();
      });

      const sortedDone = doneData.sort((a, b) => {
        if (!a.completed_at && !b.completed_at) return 0;
        if (!a.completed_at) return 1;
        if (!b.completed_at) return -1;
        return new Date(b.completed_at).getTime() - new Date(a.completed_at).getTime();
      });

      setOpenTasks(sortedOpen);
      setDoneTasks(sortedDone);
    } catch (err) {
      console.error('Failed to load tasks:', err);
    } finally {
      setLoading(false);
    }
  }, [partnerId]);

  useEffect(() => {
    loadTasks();
  }, [loadTasks]);

  const getLast30Days = (): string => {
    const date = new Date();
    date.setDate(date.getDate() - 30);
    return date.toISOString().split('T')[0];
  };

  const handleCompleteTask = async (task: CrmTask, e: React.MouseEvent) => {
    e.stopPropagation();
    setCompletingTaskId(task.id);
    try {
      await CrmTaskService.changeStatus(task.id, 'DONE');
      toast.success('Task completed');
      loadTasks();
    } catch (err) {
      console.error('Failed to complete task:', err);
      toast.error('Failed to complete task');
    } finally {
      setCompletingTaskId(null);
    }
  };

  const handleTaskClick = (task: CrmTask) => {
    if (task.offer) {
      navigate(`/offers/${task.offer}`);
    } else if (task.invoice) {
      navigate(`/invoices/${task.invoice}`);
    } else if (task.project) {
      navigate(`/projects/${task.project}`);
    } else {
      setEditingTask(task);
      setShowTaskModal(true);
    }
  };

  const handleNewTask = () => {
    setEditingTask(null);
    setShowTaskModal(true);
  };

  const handleTaskSaved = () => {
    setShowTaskModal(false);
    setEditingTask(null);
    loadTasks();
  };

  const handleQuickTaskCreated = () => {
    loadTasks();
  };

  return (
    <div
      style={{
        background: 'white',
        borderRadius: '12px',
        border: '1px solid #e0e3ef',
        overflow: 'hidden',
        marginTop: '24px',
      }}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '16px 20px',
          borderBottom: '1px solid #e0e3ef',
          background: '#f8f9fc',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <FaTasks size={16} color="#7c3aed" />
          <h3 style={{ margin: 0, fontSize: '15px', fontWeight: 600, color: '#021a4a' }}>
            Tasks for this customer
          </h3>
          {openTasks.length > 0 && (
            <span
              style={{
                padding: '2px 8px',
                borderRadius: '10px',
                fontSize: '12px',
                fontWeight: 600,
                background: '#fee2e2',
                color: '#991b1b',
              }}
            >
              {openTasks.length} open
            </span>
          )}
        </div>
        <button
          onClick={handleNewTask}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '6px 12px',
            border: '1px solid #7c3aed',
            borderRadius: '6px',
            background: 'white',
            color: '#7c3aed',
            fontSize: '13px',
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          <FaPlus size={10} />
          New task
        </button>
      </div>

      {/* Quick Task Input */}
      <QuickTaskInput
        partnerId={partnerId}
        partnerName={partnerName}
        placeholder="Add task for this customer..."
        onTaskCreated={handleQuickTaskCreated}
      />

      {/* Content */}
      <div style={{ padding: '16px 20px' }}>
        {loading ? (
          <div style={{ padding: '24px', textAlign: 'center', color: '#888' }}>
            Loading...
          </div>
        ) : openTasks.length === 0 && doneTasks.length === 0 ? (
          <div style={{ padding: '32px 20px', textAlign: 'center', color: '#888' }}>
            <FaTasks size={24} style={{ marginBottom: '8px', opacity: 0.4 }} />
            <p style={{ margin: 0, fontSize: '14px' }}>No tasks yet</p>
            <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#aaa' }}>
              Create your first task to track follow-ups
            </p>
          </div>
        ) : (
          <>
            {/* Open Tasks */}
            {openTasks.length > 0 && (
              <div style={{ marginBottom: doneTasks.length > 0 ? '20px' : 0 }}>
                <div
                  style={{
                    fontSize: '12px',
                    fontWeight: 600,
                    color: '#666',
                    marginBottom: '8px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                  }}
                >
                  Open
                </div>
                {openTasks.map((task) => (
                  <TaskRow
                    key={task.id}
                    task={task}
                    onClick={() => handleTaskClick(task)}
                    onComplete={(e) => handleCompleteTask(task, e)}
                    isCompleting={completingTaskId === task.id}
                    showCheckbox
                  />
                ))}
              </div>
            )}

            {/* Done Tasks */}
            {doneTasks.length > 0 && (
              <div>
                <button
                  onClick={() => setShowDone(!showDone)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    background: 'none',
                    border: 'none',
                    padding: '8px 0',
                    cursor: 'pointer',
                    fontSize: '12px',
                    fontWeight: 600,
                    color: '#666',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                  }}
                >
                  {showDone ? <FaChevronUp size={10} /> : <FaChevronDown size={10} />}
                  Done (last 30 days) - {doneTasks.length}
                </button>
                {showDone && (
                  <div style={{ marginTop: '8px' }}>
                    {doneTasks.map((task) => (
                      <TaskRow
                        key={task.id}
                        task={task}
                        onClick={() => handleTaskClick(task)}
                        isDone
                      />
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Task Modal */}
      <CrmTaskModal
        isOpen={showTaskModal}
        task={editingTask}
        partnerId={partnerId}
        partnerName={partnerName}
        onClose={() => {
          setShowTaskModal(false);
          setEditingTask(null);
        }}
        onSaved={handleTaskSaved}
      />
    </div>
  );
}

interface TaskRowProps {
  task: CrmTask;
  onClick: () => void;
  onComplete?: (e: React.MouseEvent) => void;
  isCompleting?: boolean;
  showCheckbox?: boolean;
  isDone?: boolean;
}

function TaskRow({
  task,
  onClick,
  onComplete,
  isCompleting,
  showCheckbox,
  isDone,
}: TaskRowProps): React.JSX.Element {
  const isOverdue = !isDone && CrmTaskService.isOverdue(task.due_date);
  const isToday = !isDone && CrmTaskService.isDueToday(task.due_date);

  return (
    <div
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        padding: '10px 12px',
        margin: '4px 0',
        cursor: 'pointer',
        background: isOverdue ? '#fef2f2' : isDone ? '#f9fafb' : 'transparent',
        borderRadius: '6px',
        borderLeft: isOverdue ? '3px solid #dc2626' : '3px solid transparent',
        transition: 'background 0.15s',
      }}
      onMouseEnter={(e) => {
        if (!isOverdue && !isDone) e.currentTarget.style.background = '#f8f9fc';
      }}
      onMouseLeave={(e) => {
        if (!isOverdue && !isDone) e.currentTarget.style.background = 'transparent';
      }}
    >
      {/* Checkbox or Done indicator */}
      {showCheckbox && onComplete ? (
        <button
          onClick={(e) => onComplete(e)}
          disabled={isCompleting}
          style={{
            width: '20px',
            height: '20px',
            borderRadius: '50%',
            border: `2px solid ${isOverdue ? '#dc2626' : '#d1d5db'}`,
            background: 'white',
            cursor: isCompleting ? 'wait' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            transition: 'all 0.15s',
          }}
          title="Mark as done"
        >
          {isCompleting && <span style={{ fontSize: '10px' }}>...</span>}
        </button>
      ) : isDone ? (
        <div
          style={{
            width: '20px',
            height: '20px',
            borderRadius: '50%',
            background: '#d1fae5',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <FaCheck size={10} color="#059669" />
        </div>
      ) : null}

      {/* Due date */}
      <div
        style={{
          width: '55px',
          flexShrink: 0,
          fontSize: '12px',
          fontWeight: 600,
          color: isDone ? '#9ca3af' : isOverdue ? '#dc2626' : isToday ? '#7c3aed' : '#666',
        }}
      >
        {isOverdue && <FaExclamationCircle size={10} style={{ marginRight: '3px' }} />}
        {CrmTaskService.formatDueDate(task.due_date)}
      </div>

      {/* Task title */}
      <div
        style={{
          flex: 1,
          fontSize: '14px',
          fontWeight: 600,
          color: isDone ? '#9ca3af' : '#021a4a',
          textDecoration: isDone ? 'line-through' : 'none',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {task.title}
      </div>

      {/* Related badge */}
      {(task.offer_number || task.invoice_number || task.project_name) && (
        <span
          style={{
            padding: '2px 6px',
            borderRadius: '4px',
            fontSize: '10px',
            fontWeight: 600,
            background: '#f3f4f6',
            color: '#6b7280',
          }}
        >
          {task.offer_number || task.invoice_number || task.project_name}
        </span>
      )}

      {/* Priority badge */}
      {!isDone && task.priority === 'HIGH' && (
        <span
          style={{
            padding: '2px 6px',
            borderRadius: '4px',
            fontSize: '10px',
            fontWeight: 600,
            background: '#fee2e2',
            color: '#991b1b',
          }}
        >
          HIGH
        </span>
      )}
    </div>
  );
}
